import { useEffect, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';

import { ScrollableWrapperProps } from './scrollable-wrapper.props';

import { useThrottledCallback } from '../../../hooks/use-throttled-callback.hook';

export const ScrollableWrapper = ({ children, ...rest }: ScrollableWrapperProps): JSX.Element => {
    const scrollableRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
        // FIX DNI-742: Sometimes initial scroll position was not at the beginning.
        if (scrollableRef.current) {
            scrollableRef.current.scrollLeft = 0;
        }
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollableRef.current) return;

        setIsDragging(true);
        setStartX(e.pageX - scrollableRef.current.offsetLeft);
        setScrollLeft(scrollableRef.current.scrollLeft);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grabbing';
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseMove = useThrottledCallback((e: React.MouseEvent) => {
        if (!isDragging || !scrollableRef.current) return;
        e.preventDefault();

        const x = e.pageX - scrollableRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        scrollableRef.current.scrollLeft = scrollLeft - walk;
    }, 16);

    return (
        <Box
            ref={scrollableRef}
            display={'grid'}
            gap={'1rem'}
            gridTemplateRows={'auto'}
            gridAutoFlow={'column'}
            scrollSnapType={'x mandatory'}
            overflowX={'auto'}
            marginLeft={'-1rem'}
            padding={'1rem 1rem 0'}
            width={'100vw'}
            scrollbar="hidden"
            css={{
                '> *': {
                    scrollSnapAlign: 'center',
                },
                cursor: 'grab',
                userSelect: 'none', // Prevent text selection during drag
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            {...rest}
        >
            {children}
        </Box>
    );
};
